"use client";

import { useState, useSyncExternalStore } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { theme } from "@/lib/theme";

interface Pattern {
  id: string;
  name: string;
  definition: string;
}

const patterns: Pattern[] = [
  {
    id: "p1",
    name: "Confirmshaming",
    definition:
      "Uses guilt or shame to pressure users into taking an action they might not otherwise take.",
  },
  {
    id: "p2",
    name: "Default Settings",
    definition:
      "Pre-selects options that benefit the platform, making it easier for users to accept than to change them.",
  },
  {
    id: "p3",
    name: "Sneak into Basket",
    definition: "Adds an item or extra cost to a user's purchase without clear consent.",
  },
  {
    id: "p4",
    name: "Bundled Consent",
    definition:
      "Requires users to agree to multiple terms or permissions together, without giving them granular control.",
  },
  {
    id: "p5",
    name: "Biased Framing",
    definition:
      "Presents choices or language in a way that nudges users toward a preferred option.",
  },
];

let cachedShuffle: string[] | null = null;
function getShuffledSnapshot(): string[] {
  if (!cachedShuffle) {
    cachedShuffle = patterns.map((p) => p.definition).sort(() => Math.random() - 0.5);
  }
  return cachedShuffle;
}
const emptyDefs: string[] = [];
const noop = () => () => {};

export const Quiz = () => {
  const [correctMatches, setCorrectMatches] = useState<Map<string, string>>(new Map());
  const [flashingPattern, setFlashingPattern] = useState<{ id: string; correct: boolean } | null>(
    null
  );
  const [draggedPatternId, setDraggedPatternId] = useState<string | null>(null);
  const shuffledDefinitions = useSyncExternalStore(noop, getShuffledSnapshot, () => emptyDefs);

  const handleDrop = (patternId: string, definition: string) => {
    const correctPattern = patterns.find((p) => p.id === patternId);
    const isCorrect = correctPattern && correctPattern.definition === definition;

    if (isCorrect) {
      // Flash green and then hide
      setFlashingPattern({ id: patternId, correct: true });
      setTimeout(() => {
        setCorrectMatches((prev) => new Map(prev).set(patternId, definition));
        setFlashingPattern(null);
      }, 600);
    } else {
      // Flash red
      setFlashingPattern({ id: patternId, correct: false });
      setTimeout(() => {
        setFlashingPattern(null);
      }, 600);
    }
  };

  // Touch event handlers for mobile
  const handleTouchStart = (patternId: string) => {
    setDraggedPatternId(patternId);
  };

  const handleTouchEnd = (e: React.TouchEvent, definition: string) => {
    if (draggedPatternId) {
      handleDrop(draggedPatternId, definition);
      setDraggedPatternId(null);
    }
  };

  const availablePatterns = patterns.filter((p) => !correctMatches.has(p.id));

  // Helper function to get the pattern name for a definition
  const getMatchedPatternName = (definition: string): string | undefined => {
    for (const [patternId, def] of correctMatches.entries()) {
      if (def === definition) {
        const pattern = patterns.find((p) => p.id === patternId);
        return pattern?.name;
      }
    }
    return undefined;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: {
          xs: 2,
          sm: 3,
        },
        background: theme.colors.gray[50],
        borderRadius: theme.borderRadius.lg,
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        width: "100%",
        maxWidth: {
          xs: "100%",
          sm: "900px",
        },
        margin: "0 auto",
        marginY: {
          xs: 4,
          sm: 6,
        },
      }}
    >
      <Typography
        variant="h5"
        component="h5"
        sx={{
          marginBottom: 1.5,
          textAlign: "center",
        }}
      >
        Match the Dark Pattern
      </Typography>

      <Typography
        variant="body2"
        sx={{
          marginBottom: 3,
          textAlign: "center",
          fontFamily: theme.fonts.sans,
          width: {
            xs: "100%",
            sm: "75%",
          },
        }}
      >
        Drag a <strong>harmful design patterns</strong> name onto its correct{" "}
        <strong>definition</strong>. Test your knowledge before diving deeper.
      </Typography>

      <Grid
        container
        spacing={2}
        sx={{
          width: "100%",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
        }}
      >
        {/* Left column — pattern names */}
        <Grid
          size={{
            xs: 12,
            sm: 5,
          }}
        >
          <Typography
            variant="h6"
            component="h6"
            sx={{
              marginBottom: 1,
            }}
          >
            Patterns
          </Typography>
          {availablePatterns.map((p) => {
            const isFlashing = flashingPattern?.id === p.id;
            const flashColor = flashingPattern?.correct
              ? `${theme.colors.successGreen}40` // 25% opacity
              : `${theme.colors.errorRed}40`; // 25% opacity

            return (
              <Box
                key={p.id}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("patternId", p.id)}
                onTouchStart={() => handleTouchStart(p.id)}
                sx={{
                  marginBottom: 1,
                  background: isFlashing ? flashColor : theme.colors.white,
                  borderRadius: theme.borderRadius.md,
                  padding: 1,
                  border: `1px solid ${theme.colors.gray[200]}`,
                  cursor: "grab",
                  transition: "all 0.3s ease",
                  color: isFlashing ? theme.colors.black : "inherit",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  "&:active": {
                    cursor: "grabbing",
                  },
                  "&:hover": {
                    borderColor: theme.colors.gray[300],
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "inherit",
                    fontFamily: theme.fonts.sans,
                    fontWeight: theme.fonts.weights.bold,
                  }}
                >
                  {p.name}
                </Typography>
              </Box>
            );
          })}
        </Grid>

        {/* Right column — definitions */}
        <Grid
          size={{
            xs: 12,
            sm: 7,
          }}
        >
          <Typography
            variant="h6"
            component="h6"
            sx={{
              marginBottom: 1,
            }}
          >
            Definitions
          </Typography>
          {shuffledDefinitions.map((def, i) => {
            const matchedPatternName = getMatchedPatternName(def);

            return (
              <Box
                key={i}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const patternId = e.dataTransfer.getData("patternId");
                  handleDrop(patternId, def);
                }}
                onTouchEnd={(e) => handleTouchEnd(e, def)}
                sx={{
                  minHeight: 50,
                  marginBottom: 1,
                  background: theme.colors.white,
                  borderRadius: theme.borderRadius.md,
                  padding: 1,
                  border: `1px solid ${theme.colors.gray[200]}`,
                  textAlign: "left",
                  "&:hover": {
                    borderColor: theme.colors.gray[300],
                  },
                }}
              >
                {matchedPatternName && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: theme.fonts.weights.bold,
                      marginBottom: 0.5,
                      color: theme.colors.black,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <span style={{ color: theme.colors.successGreen }}>✓</span>
                    {matchedPatternName}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: theme.fonts.sizes.sm,
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {def}
                </Typography>
              </Box>
            );
          })}
        </Grid>
      </Grid>

      {correctMatches.size === patterns.length && (
        <Box
          sx={{
            marginTop: 3,
            padding: 2,
            background: theme.colors.successGreen,
            color: theme.colors.white,
            borderRadius: theme.borderRadius.md,
            textAlign: "center",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: theme.fonts.weights.bold,
              color: "inherit",
              fontFamily: theme.fonts.sans,
            }}
          >
            🎉 All correct! Great job!
          </Typography>
        </Box>
      )}
    </Box>
  );
};
